import { useTranslation } from "react-i18next";
import OrderService from "../../api/services/orders.service";
import DataTable, {
  Column,
} from "../../components/common/data-table/DataTable";
import Loading from "../../components/common/loading/Loading";
import TitleUpdater from "../../components/common/title/TitleUpdater";
import statuses from "../../constants/status";
import { formatDate } from "../../utils/date";
import styles from "./Delivery.module.scss";
import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import i18n from "../../i18n";
import ProductDetailModal from "../../components/product-detail-modal/ProductDetailModal";

type Order = {
  id: number;
  product: string;
  color: string;
  storage: string;
  total: number;
  deliverry_price: number;
  created_at: string;
  address: string;
  status: string;
};

type TabType = "1" | "2" | "3";

const Delivery = () => {
  const [activeTab, setActiveTab] = useState<TabType>("1");
  const [data, setData] = useState<Order[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [statusCounts, setStatusCounts] = useState({ "1": 0, "2": 0, "3": 0 });
  const [actionLoading, setActionLoading] = useState<number | null>(null);
  const { t } = useTranslation("translation");
  const [selectedProduct, setSelectedProduct] = useState<any>(null);
  const [isProductModalOpen, setIsProductModalOpen] = useState(false);

  const [pagination, setPagination] = useState({
    page: 1,
    count: 10,
    total: 0,
  });

  const fetchOrders = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await OrderService.getAllOrders(
        pagination.page,
        pagination.count,
        activeTab,
      );
      const orders = response.data?.data || [];
      const total = response.data?.total || orders.length;
      const resultCounts = response.data?.result_counts || [];
      const counts = { "1": 0, "2": 0, "3": 0 };
      resultCounts.forEach((item: any) => {
        if (item.status in counts) {
          counts[item.status as keyof typeof counts] = item.count;
        }
      });

      setStatusCounts(counts);
      setData(orders);

      setPagination((prev) => ({ ...prev, total }));
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        t("error_message");

      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.count, activeTab, t]);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  const changeOrderStatus = async (id: number, currentStatus: string) => {
    setActionLoading(id);
    try {
      await OrderService.chageStatus(id);
      const successMessages = {
        "1": t("delivery_messages.success.confirmed"),
        "2": t("delivery_messages.success.marked_ready"),
        "3": t("delivery_messages.success.sent_to_admin"),
      };
      toast.success(
        successMessages[currentStatus as keyof typeof successMessages] ||
          t("delivery_messages.success.default"),
      );
      fetchOrders();
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        t("error_message");
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setActionLoading(null);
    }
  };

  const handleConfirmAndPack = (id: number) => {
    changeOrderStatus(id, "1");
  };

  const handleMarkReady = (id: number) => {
    changeOrderStatus(id, "2");
  };

  const handleSendToAdmin = (id: number) => {
    changeOrderStatus(id, "3");
  };

  const StatusButton = ({ status, id }: { status: string; id: number }) => {
    const isLoading = actionLoading === id;

    switch (status) {
      case "1":
        return (
          <button
            className={styles.confirmBtn}
            onClick={() => handleConfirmAndPack(id)}
            disabled={isLoading}
          >
            {isLoading ? t("buttons.processing") : t("buttons.confirm_pack")}
          </button>
        );
      case "2":
        return (
          <button
            className={styles.readyBtn}
            onClick={() => handleMarkReady(id)}
            disabled={isLoading}
          >
            {isLoading ? t("buttons.processing") : t("buttons.mark_ready")}
          </button>
        );
      case "3":
        return (
          <button
            className={styles.adminBtn}
            onClick={() => handleSendToAdmin(id)}
            disabled={isLoading}
          >
            {isLoading ? t("buttons.processing") : t("buttons.send_to_admin")}
          </button>
        );
      default:
        return (
          <span>{statuses[status as keyof typeof statuses] || status}</span>
        );
    }
  };

  const handlePageChange = (newPage: number) => {
    if (
      newPage >= 1 &&
      newPage <= Math.ceil(pagination.total / pagination.count)
    ) {
      setPagination((prev) => ({ ...prev, page: newPage }));
    }
  };

  const handleLimitChange = (newLimit: number) => {
    setPagination((prev) => ({ ...prev, count: newLimit, page: 1 }));
  };

  const handleTabChange = (tab: TabType) => {
    setActiveTab(tab);
  };

  useEffect(() => {
    setPagination((prev) => ({ ...prev, page: 1 }));
  }, [activeTab]);

  const getLocalizedName = (row: any, lang: string): string => {
    const priorities: Record<string, (keyof typeof row)[]> = {
      az: ["name_az", "name_en", "name_de", "name_es"],
      en: ["name_en", "name_az", "name_de", "name_es"],
      es: ["name_es", "name_en", "name_az", "name_de"],
      de: ["name_de", "name_en", "name_az", "name_es"],
    };

    const order = priorities[lang] || priorities["en"];

    for (const key of order) {
      const value = row[key];
      if (value && typeof value === "string" && value.trim()) {
        return value;
      }
    }

    return "Unnamed Variant";
  };

  const handleProductClick = (row: any) => {
    setSelectedProduct(row);
    setIsProductModalOpen(true);
  };

  const columns: Column[] = [
    {
      key: "user",
      header: t("columns.client") || "Client",
      render: (_, row) => (
        <p className={styles.content}>
          {row?.user?.first_name} {row?.user?.last_name}
        </p>
      ),
    },
    {
      key: "sku",
      header: t("columns.sku") || "SKU",
      render: (_, row) => (
        <p className={styles.content}>{row?.product_variant?.sku || "-"}</p>
      ),
    },
    {
      key: "product",
      header: t("columns.product") || "Product",
      render: (_, row) => {
        const productName = getLocalizedName(
          row?.product_variant || {},
          i18n.language,
        );
        return (
          <p
            className={`${styles.content} ${styles.productName}`}
            onClick={() => handleProductClick(row)}
          >
            {productName || "-"}
          </p>
        );
      },
    },
    {
      key: "images",
      header: t("columns.images") || "Images",
      render: (_, row) => {
        const images = row?.product_variant?.images || [];
        if (images.length === 0) return <p className={styles.content}>-</p>;
        const firstImage = images[0];
        return (
          <div className={styles.imagesWrapper}>
            <img
              src={firstImage?.image}
              alt="product"
              className={styles.productImage}
            />
            {images.length > 1 && (
              <span className={styles.imageCount}>+{images.length - 1}</span>
            )}
          </div>
        );
      },
    },
    {
      key: "price",
      header: t("columns.price") || "Price",
      render: (_, row) => (
        <div className={styles.price_content}>
          <p>
            {`${row?.price?.toFixed(2) || "0.00"} ${row?.currency_name || ""}`}
          </p>
          <p>
            {row?.quantity || 0}{" "}
            {t("product_count_label", { count: row?.quantity || 0 })}
          </p>
        </div>
      ),
    },
    {
      key: "created_at",
      header: t("columns.date") || "Date",
      render: (_, row) => formatDate(row?.created),
    },
    {
      key: "status",
      header: t("columns.status") || "Status",
      render: (value, row) => {
        return <StatusButton status={value} id={row.id} />;
      },
    },
  ];

  const tabLabels = {
    "1": t("tabs.incoming"),
    "2": t("tabs.packing"),
    "3": t("tabs.ready"),
  };

  if (loading) {
    return <Loading />;
  }

  return (
    <div className={styles.delivery}>
      <TitleUpdater title={t("delivery")} />
      <div className={styles.section_header}>
        <h2 className={styles.title}>{t("delivery")}</h2>
      </div>
      <div className={styles.tabs}>
        <button
          className={`${styles.tab} ${activeTab === "1" ? styles.active : ""}`}
          onClick={() => handleTabChange("1")}
        >
          {tabLabels["1"]} ({statusCounts["1"]})
        </button>
        <button
          className={`${styles.tab} ${activeTab === "2" ? styles.active : ""}`}
          onClick={() => handleTabChange("2")}
        >
          {tabLabels["2"]} ({statusCounts["2"]})
        </button>
        <button
          className={`${styles.tab} ${activeTab === "3" ? styles.active : ""}`}
          onClick={() => handleTabChange("3")}
        >
          {tabLabels["3"]} ({statusCounts["3"]})
        </button>
      </div>
      {error && (
        <div className={styles.error}>
          {error}
          <button onClick={fetchOrders} className={styles.retryBtn}>
            {t("buttons.retry")}
          </button>
        </div>
      )}
      <div className={styles.tabContent}>
        {!loading && !error && (
          <DataTable
            data={data}
            columns={columns}
            paginationData={{
              total: pagination.total,
              page: pagination.page,
              limit: pagination.count,
              onPageChange: handlePageChange,
              onLimitChange: handleLimitChange,
            }}
            noDataMessage={t("delivery_messages.no_data") || ""}
          />
        )}
      </div>
      {selectedProduct && (
        <ProductDetailModal
          isOpen={isProductModalOpen}
          onClose={() => {
            setIsProductModalOpen(false);
            setSelectedProduct(null);
          }}
          productVariant={selectedProduct?.product_variant}
          orderPrice={selectedProduct?.price}
          orderCurrency={selectedProduct?.currency_name}
          orderQuantity={selectedProduct?.quantity}
        />
      )}
    </div>
  );
};

export default Delivery;
